// Click feedback: a button shows the work its click started. A click "arms" the button for a short window; every
// request the page starts in that window (through fetch, so `api()`, downloads and exports alike) is attached to it.
// The button stays busy until they have all settled, then shows done or failed. A request started right after one
// settles (a mutation's refetch, a chained call) extends the same click, so "done" means the screen is up to date.
// No request in the window: the click only did something on the page (opened a menu, switched a tab).

/** How long after a click (or after its last request settled) a new request still belongs to it. */
export const CLICK_WINDOW_MS = 400;

export type ClickOutcome = 'done' | 'failed' | 'local';

interface Click {
  pending: number;
  failed: boolean;
  until: number;
  timer: ReturnType<typeof setTimeout> | null;
  finish: (o: ClickOutcome) => void;
  hadRequest: boolean;
}

let armed: Click | null = null;

function settleLater(c: Click) {
  if (c.timer) clearTimeout(c.timer);
  c.until = Date.now() + CLICK_WINDOW_MS;
  c.timer = setTimeout(() => {
    if (c.pending > 0) return;
    if (armed === c) armed = null;
    c.finish(c.hadRequest ? (c.failed ? 'failed' : 'done') : 'local');
  }, CLICK_WINDOW_MS);
}

/** Starts a click; `finish` is called once with the outcome. Returns a function that drops the click (unmount). */
export function armClick(finish: (o: ClickOutcome) => void): () => void {
  const c: Click = { pending: 0, failed: false, until: 0, timer: null, finish, hadRequest: false };
  armed = c;
  settleLater(c);
  return () => {
    if (c.timer) clearTimeout(c.timer);
    c.finish = () => {};
    if (armed === c) armed = null;
  };
}

/** Attaches a request to the armed click, if one is armed and its window is open. `ok` reads success from the result. */
export function trackRequest<T>(p: Promise<T>, ok: (v: T) => boolean = () => true): void {
  const c = armed;
  if (!c || Date.now() > c.until) return;
  c.pending++;
  c.hadRequest = true;
  if (c.timer) clearTimeout(c.timer);
  c.until = Number.POSITIVE_INFINITY;
  p.then(
    v => {
      if (!ok(v)) c.failed = true;
    },
    () => {
      c.failed = true;
    },
  ).finally(() => {
    c.pending--;
    if (c.pending === 0) settleLater(c);
  });
}

/** Wraps `window.fetch` once so every request can be attached to the click that started it. */
export function installClickFeedback(w: { fetch: typeof fetch } = window) {
  const original = w.fetch.bind(w);
  w.fetch = ((...args: Parameters<typeof fetch>) => {
    const p = original(...args);
    trackRequest(p, r => r.ok);
    return p;
  }) as typeof fetch;
}
